import React, { useEffect, useCallback, useRef, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import deepMerge from 'lodash/merge';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { PanelListCss } from '../../styles';
import { SyiReducerState, updateElementBlurChange, updateSyi } from '../../reducers/syi';
import { dtlSyi } from '../../services/dtl-syi';
import { computeListingDetails } from '../../services/syi-model';
import type { SyiState } from '../../reducers';
import { safeSetData, safeGetData } from '@gumtree/ui-library/src/utils/data-service';

import SelectAttrbuteModel, {
    AttributeStateClientSide,
    AttributeStateServerSide,
    SelectModelProps,
} from './features/select-attribute-model';
import { PanelNameMapping } from '../all-components-definitions';
import type { SearchableListModalProps } from '@gumtree/ui-library/src/searchable-list-modal/searchable-list-modal';

export type AttributeStateNameMapping = {
    selectedValue: string;
    selectedDisplayName: string;
    type?: 'input' | 'checkbox' | 'search-select';
    default?: string | number | boolean;
    isInvalid?: string;
    hasSelected?: string;
    elName?: string;
};

interface Props<T extends string> {
    panelName: string;
    attribute: string;
    attributeStateNameMapping?: { [key in T]: AttributeStateNameMapping };
    panelAttributes: {
        [panelName: string]: SyiPanelAttribute<T>;
    };
    hideTitle?: boolean;
    onValidate?: () => any;
    className?: string;
    searchSelectProps?: Partial<SearchableListModalProps>;
}

export interface SyiPanelAttribute<T extends string> {
    attributes: { [key in T]: AttributeStateServerSide };
    formAttributes: {
        [key in T]: {
            attributeName: string;
            attributeDisplayName: string;
            value: string;
            valueDisplayName: string;
        };
    };
    highPriority: boolean;
    id: string;
    label: string;
    panelId: string;
}

const ga4EventSelect = (ga4Label, key) => {
    if (!ga4Label) {
        return;
    }

    trackGA4Event<GA4.PostAdInteractionEvent>({
        event: 'ad_posted_form_interactions',
        formName: 'syi-form',
        formStep: `change attribute[${key} saved][${ga4Label}]`,
        ...computeListingDetails(),
    });
};

export const getUpdatesForAttributeFactory =
    (attributeStateNameMapping) =>
    (
        key: string,
        value: string,
        displayValue: string,
        isInitial = false // we should not show the error message when enter for the first time
    ): Partial<SyiReducerState> | undefined => {
        const name: AttributeStateNameMapping = attributeStateNameMapping[key];
        const updatedState: {
            [key: string]: string | boolean | AttributeStateServerSide['selectedValue'] | undefined;
        } = {};
        safeSetData(updatedState, name.selectedValue, value);
        safeSetData(updatedState, name.selectedDisplayName, displayValue);
        if (!isInitial && name.isInvalid) {
            safeSetData(updatedState, name.isInvalid, !value);
        }
        if (name.hasSelected) {
            safeSetData(updatedState, name.hasSelected, !!value);
        }

        return updatedState;
    };

const PanelSelectListing = <T extends string>(props: Props<T>) => {
    const panelChanged = useRef(false);
    const dispatch = useDispatch();
    const panel = props.panelAttributes[props.panelName];

    const attributeStateNameMapping = useMemo(() => {
        if (props.attributeStateNameMapping) return props.attributeStateNameMapping;
        const root = createAttributeMap(props.panelName);
        return Object.fromEntries(
            Object.entries(panel?.attributes).map(([key, value]) => {
                const attributeStateServerSide = value as AttributeStateServerSide;
                return [
                    key,
                    root(key, {
                        required: attributeStateServerSide.mandatory,
                        defaultValue: attributeStateServerSide.mandatory
                            ? undefined
                            : attributeStateServerSide.default,
                    }),
                ];
            })
        ) as Record<string, AttributeStateNameMapping>;
    }, [props.attributeStateNameMapping, panel?.attributes, props.panelName]);

    const getUpdatesForAttribute = getUpdatesForAttributeFactory(attributeStateNameMapping);

    const { syi } = useSelector(({ syi }: SyiState) => ({
        syi,
    }));

    useEffect(() => {
        Object.entries(panel?.formAttributes).forEach(([key, attr]) => {
            const value = (attr as { value: string })?.value;
            const displayValue = (attr as { valueDisplayName: string })?.valueDisplayName || '';

            const updates = getUpdatesForAttribute(key, value, displayValue, true);

            updates && dispatch(updateSyi(updates));
        });
    }, [panel?.formAttributes, attributeStateNameMapping]);

    useEffect(() => {
        const onSubmit = () => {
            if (props.onValidate) return props.onValidate();
            let updatedState = {};
            Object.keys(attributeStateNameMapping).forEach((key) => {
                const i = attributeStateNameMapping[key];
                const {
                    hasSelected,
                    isInvalid,
                    default: defaultValue,
                    selectedValue,
                } = i as AttributeStateNameMapping;
                const inputValue = safeGetData(selectedValue, syi);
                if (hasSelected && !safeGetData(hasSelected, syi)) {
                    isInvalid && safeSetData(updatedState, isInvalid, true);
                } else if (!inputValue && !isInvalid && defaultValue) {
                    safeSetData(updatedState, selectedValue, defaultValue);
                }
            });
            Object.keys(updatedState).length > 0 && dispatch(updateSyi(updatedState));
        };
        window.addEventListener('syi-from-submit', onSubmit);
        return () => {
            window.removeEventListener('syi-from-submit', onSubmit);
        };
    }, [syi.panelAttributesConfig, attributeStateNameMapping]);

    const { expandCategory, clientSidePhoneAttributeStates } = useSelector(
        ({ syi }: SyiState) => ({
            expandCategory: syi.expandCategory,
            clientSidePhoneAttributeStates: Object.fromEntries(
                Object.entries(panel?.formAttributes).map(([key]) => {
                    const nameMappingState = attributeStateNameMapping[
                        key
                    ] as AttributeStateNameMapping;
                    const value = panel?.formAttributes[key] as AttributeStateNameMapping;
                    return [
                        key as T,
                        {
                            selectedValue: safeGetData(nameMappingState.selectedValue, syi) ?? '', // need to cast to "" as value sometimes comes back as "null", if null or undefined,when deep merger this value will be `{present: fasle}`
                            selectedDisplayName:
                                safeGetData(nameMappingState.selectedDisplayName, syi) ?? undefined, // need to cast to "undefined" as value sometimes comes back as "null"
                            ...(nameMappingState.isInvalid && {
                                isInvalid: safeGetData(nameMappingState.isInvalid, syi),
                            }),
                            ...(nameMappingState.hasSelected && {
                                hasSelected: safeGetData(nameMappingState.hasSelected, syi),
                            }),
                            elName: value.elName,
                        } as AttributeStateClientSide,
                    ];
                })
            ),
        }),
        shallowEqual
    );
    const attributesLength = Object.values(panel.attributes).length;
    const mergedPanelAttributes = Object.values<AttributeStateServerSide>(
        deepMerge({}, panel.attributes, clientSidePhoneAttributeStates) as {
            [key in T]: AttributeStateServerSide & AttributeStateClientSide;
        }
    ).sort(
        (a, b) => (a.sort ?? attributesLength) - (b.sort ?? attributesLength)
    ) as SelectModelProps[];
    useEffect(() => {
        const selectedValue = Object.values(clientSidePhoneAttributeStates).some(
            (attr) => !!attr.selectedValue
        );
        if (selectedValue && panelChanged.current) {
            dtlSyi.submitDraft();
            panelChanged.current = false;
            dispatch(
                updateElementBlurChange(PanelNameMapping.MOBILE_PHONES_ATTRIBUTES_PANEL, '') as any
            );
        }
    }, [clientSidePhoneAttributeStates]);

    const saveChanges = useCallback(
        (value: string, displayValue: string, key: T) => {
            const updates = getUpdatesForAttribute(key, value, displayValue);
            updates && dispatch(updateSyi(updates));
            panelChanged.current = true;
            value && ga4EventSelect(value.replaceAll('_', ' '), key);
        },
        [dispatch, ga4EventSelect, clientSidePhoneAttributeStates]
    );
    const shouldShowTitle = !props.hideTitle && panel.label;
    return (
        <PanelListCss
            className={cx({ 'is-closed': expandCategory }, 'panel', props.className)}
            data-validation-group={decodeURI(panel.id)}
            key={props.panelName}
        >
            {shouldShowTitle && (
                <h2 className="panel-title">
                    <span className="panel-validation" />
                    {panel.label}
                </h2>
            )}
            <div
                className="panel-content"
                data-testid="panel-content"
                {...(props.attribute ? { [`data-${props.attribute}`]: true } : {})}
                style={{ marginTop: shouldShowTitle ? '0' : '8px' }}
            >
                {mergedPanelAttributes.map((templateData) => {
                    return (
                        <SelectAttrbuteModel
                            {...templateData}
                            key={templateData.id}
                            searchSelectProps={props.searchSelectProps}
                            saveChanges={(value, displayValue) =>
                                saveChanges(value, displayValue, templateData.id as T)
                            }
                        />
                    );
                })}
            </div>
        </PanelListCss>
    );
};

export default PanelSelectListing;

export function createAttributeMap(root: string) {
    return (
        attribute: string,
        {
            required,
            defaultValue,
        }: {
            required?: boolean;
            defaultValue?: string | number | boolean;
            defaultDiaplayName?: string;
        } = {}
    ) => {
        const attributeMap: AttributeStateNameMapping = {
            selectedValue: `panelAttributesConfig.${root}.${attribute}.value`,
            selectedDisplayName: `panelAttributesConfig.${root}.${attribute}.displayName`,
        };
        if (defaultValue) {
            attributeMap.default = defaultValue;
        }
        if (required) {
            attributeMap.isInvalid = `panelAttributesConfig.${root}.${attribute}.error`;
            attributeMap.hasSelected = `panelAttributesConfig.${root}.${attribute}.hasSelected`;
        }
        return attributeMap;
    };
}

export const CommonAttributeKeys = [
    'condition_with_only_new_value',
    'baby_kids_stuff_brand',
    'baby_nappy_size',
    'baby_car_seat_size',
    'baby_bedding_size',
    'age_group',
    'sport_stuff_brand',
    'sport_shoe_size',
    'common_clothing_size_optional',
    'appliance_brand',
] as string[];
